import React, { useCallback, useEffect, useState } from "react";
import NavBar from "../components/NavBar";
import API from "../api";
import { Plus, Trash2, Edit2, X, Users2, Upload, ChevronDown, ChevronUp } from "lucide-react";

const ROLES = [
  { id: "batter", label: "Batter" },
  { id: "bowler", label: "Bowler" },
  { id: "allrounder", label: "All-rounder" },
];

const roleColor = (r) => r === "bowler" ? "bg-red-500/15 text-red-300 border-red-800"
                    : r === "allrounder" ? "bg-amber-500/15 text-amber-300 border-amber-800"
                    : "bg-blue-500/15 text-blue-300 border-blue-800";

const EMPTY = { name: "", short_name: "", color: "#142168", logo: "", players: [] };

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = create, team = edit
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { setTeams((await API.get("/teams")).data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this team? Any tournaments referencing it will keep the reference but broken.")) return;
    await API.delete(`/teams/${id}`); await load();
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <NavBar subtitle="Teams Library" />
      <main className="pt-24 pb-16 max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-4xl font-extrabold uppercase tracking-tight">Teams Library</h1>
            <p className="text-slate-400 mt-1">Save teams once — reuse them across every match and tournament</p>
          </div>
          <button onClick={() => setEditing({ ...EMPTY })} data-testid="create-team-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold">
            <Plus size={16} /> New Team
          </button>
        </div>

        {loading ? <div className="p-10 text-center text-slate-400">Loading...</div> :
          teams.length === 0 ? (
            <div className="glass rounded-2xl border border-slate-800 p-12 text-center">
              <Users2 className="mx-auto text-blue-500 mb-4" size={40}/>
              <div className="font-heading text-2xl font-bold uppercase">No teams yet</div>
              <p className="text-slate-400 mt-2">Add your first team so you don't retype lineups every match.</p>
              <button onClick={() => setEditing({ ...EMPTY })} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold">
                <Plus size={16}/> Add First Team
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teams.map(t => (
                <div key={t.id} data-testid={`team-card-${t.id}`} className="glass rounded-xl border border-slate-800 overflow-hidden">
                  <div className="p-4 flex items-start gap-3">
                    <div className="w-14 h-14 rounded-lg flex-shrink-0 flex items-center justify-center font-heading font-black text-xl text-white"
                      style={{ backgroundColor: t.color }}>
                      {t.logo ? <img src={t.logo} alt="" className="w-full h-full object-contain rounded-lg" /> : t.short_name.slice(0,3)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-xl font-extrabold uppercase truncate">{t.name}</div>
                      <div className="text-xs text-slate-400">{t.short_name} · {t.players.length} players</div>
                    </div>
                    <button onClick={() => setEditing(t)} data-testid={`edit-team-${t.id}`}
                      className="p-2 rounded bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 border border-blue-800"><Edit2 size={14}/></button>
                    <button onClick={() => remove(t.id)} data-testid={`delete-team-${t.id}`}
                      className="p-2 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-900"><Trash2 size={14}/></button>
                  </div>
                  {t.players.length > 0 && (
                    <>
                      <button onClick={() => setExpanded(e => ({ ...e, [t.id]: !e[t.id] }))}
                        className="w-full px-4 py-2 border-t border-slate-800 text-xs text-slate-400 hover:bg-slate-800 flex items-center justify-center gap-1">
                        {expanded[t.id] ? <><ChevronUp size={14}/> Hide players</> : <><ChevronDown size={14}/> Show players</>}
                      </button>
                      {expanded[t.id] && (
                        <div className="p-3 border-t border-slate-800 bg-slate-900/60 grid grid-cols-1 gap-1.5 max-h-64 overflow-y-auto">
                          {t.players.map((p, i) => (
                            <div key={i} className="flex items-center justify-between text-sm px-2 py-1 rounded bg-slate-800/60">
                              <span className="text-slate-200 truncate">{i + 1}. {p.name}</span>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${roleColor(p.role)}`}>{p.role}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )
        }
      </main>

      {editing && <TeamModal team={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function TeamModal({ team, onClose, onSaved }) {
  const isEdit = !!team.id;
  const [form, setForm] = useState({
    name: team.name || "", short_name: team.short_name || "",
    color: team.color || "#142168", logo: team.logo || "",
    players: team.players || [],
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const addPlayer = () => setForm(f => ({ ...f, players: [...f.players, { name: "", role: "batter" }] }));
  const removePlayer = (i) => setForm(f => ({ ...f, players: f.players.filter((_, idx) => idx !== i) }));
  const updatePlayer = (i, key, val) => setForm(f => ({
    ...f, players: f.players.map((p, idx) => idx === i ? { ...p, [key]: val } : p),
  }));

  const onLogo = (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 300 * 1024) { setErr("Logo too large (max 300KB)"); return; }
    const r = new FileReader();
    r.onload = () => setForm(f => ({ ...f, logo: r.result }));
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const payload = { ...form, players: form.players.filter(p => p.name.trim()) };
      if (isEdit) await API.patch(`/teams/${team.id}`, payload);
      else await API.post("/teams", payload);
      onSaved(); onClose();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="glass rounded-2xl border border-slate-700 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-extrabold uppercase">{isEdit ? "Edit Team" : "New Team"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded"><X size={18}/></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Full name (INDIA)" data-testid="team-form-name"
              className="px-3 py-2 rounded bg-slate-800 border border-slate-700"/>
            <input required value={form.short_name} onChange={e=>setForm({...form,short_name:e.target.value})} placeholder="Short (IND)" data-testid="team-form-short"
              className="px-3 py-2 rounded bg-slate-800 border border-slate-700"/>
          </div>
          <div className="grid grid-cols-2 gap-3 items-center">
            <div>
              <label className="text-xs uppercase text-slate-400 font-semibold">Theme color</label>
              <input type="color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})} data-testid="team-form-color"
                className="w-full h-10 rounded bg-slate-800 border border-slate-700 cursor-pointer"/>
            </div>
            <div>
              <label className="text-xs uppercase text-slate-400 font-semibold flex items-center gap-1"><Upload size={12}/> Logo (PNG/JPG, max 300KB)</label>
              <input type="file" accept="image/*" onChange={onLogo} data-testid="team-form-logo"
                className="w-full text-sm text-slate-300 file:mr-2 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-slate-700 file:text-white"/>
              {form.logo && <div className="mt-1 text-xs text-emerald-400">✓ Logo attached</div>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-slate-200">Players ({form.players.length})</label>
              <button type="button" onClick={addPlayer} data-testid="add-player-button"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-xs font-semibold">
                <Plus size={12}/> Add player
              </button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
              {form.players.map((p, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-slate-500 text-xs w-6">{i+1}.</span>
                  <input value={p.name} onChange={e=>updatePlayer(i,"name",e.target.value)} placeholder="Player name" data-testid={`player-name-${i}`}
                    className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm"/>
                  <select value={p.role} onChange={e=>updatePlayer(i,"role",e.target.value)} data-testid={`player-role-${i}`}
                    className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
                    {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                  <button type="button" onClick={()=>removePlayer(i)} data-testid={`remove-player-${i}`}
                    className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20"><Trash2 size={12}/></button>
                </div>
              ))}
              {form.players.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No players yet — click "Add player" (no limit on how many).</div>}
            </div>
          </div>

          {err && <div className="text-sm p-2 rounded bg-red-500/10 border border-red-900 text-red-300">{err}</div>}
          <button type="submit" disabled={busy} data-testid="team-form-submit"
            className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 font-semibold">
            {busy ? "Saving..." : isEdit ? "Save Changes" : "Create Team"}
          </button>
        </form>
      </div>
    </div>
  );
}
